import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ChevronLeft,
  CircleUserRound,
  LogOut,
  Menu,
  Moon,
  PanelLeft,
  Search,
  Sun,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { CONFIG } from "@/config";
import { useSidebar } from "@/contexts/SidebarContext";
import { useThemeMode } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import SearchDialog from "@/components/Dialogs/search";
import { navItems } from "./navItems";

function getActiveTo(pathname: string): string | null {
  if (pathname === "/") return "/";

  let best: string | null = null;
  for (const item of navItems) {
    if (item.to === "/") continue;
    if (pathname === item.to || pathname.startsWith(item.to + "/")) {
      if (!best || item.to.length > best.length) best = item.to;
    }
  }
  return best;
}

export function Sidebar() {
  const { collapsed, toggleCollapsed, isMobile, mobileOpen, closeMobile } =
    useSidebar();
  const { themeMode, toggleTheme } = useThemeMode();
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTo = getActiveTo(location.pathname);
  const [searchOpen, setSearchOpen] = useState(false);

  // On mobile the drawer is always full-width (never in "rail" mode).
  const isRail = collapsed && !isMobile;

  const handleNavigate = (to: string) => {
    navigate(to);
    if (isMobile) closeMobile();
  };

  // Icon buttons sitting on the green header/footer bars.
  const greenIconClass = "text-white hover:bg-white/15 hover:text-white";
  // Icon buttons sitting on the light grey body (collapsed rail search).
  const bodyIconClass =
    "text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-[#8ba1b7] dark:hover:bg-white/5 dark:hover:text-white";

  const displayName = user?.name || user?.nickname || user?.email || "Account";
  const avatarNode = user?.picture ? (
    <img
      src={user.picture}
      alt=""
      className="size-9 shrink-0 rounded-full object-cover ring-2 ring-white/25"
    />
  ) : (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/15">
      <CircleUserRound className="size-5" />
    </span>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          "z-50 flex shrink-0 flex-col border-r border-slate-200 bg-slate-100 text-slate-700 transition-all duration-200 dark:border-white/10 dark:bg-[#191B1F] dark:text-[#8ba1b7]",
          isMobile
            ? "fixed inset-y-0 left-0 w-64 shadow-2xl"
            : "sticky top-0 h-screen",
          isMobile && !mobileOpen && "-translate-x-full",
          isRail ? "md:w-16" : "md:w-64",
        )}
      >
        {/* Brand header */}
        <div
          className={cn(
            "flex h-14 items-center gap-2 bg-brand-primary px-3 text-white",
            isRail && "justify-center px-2",
          )}
        >
          {!isRail && (
            <Link
              to="/"
              onClick={() => isMobile && closeMobile()}
              className="flex min-w-0 items-center gap-2 text-white no-underline"
            >
              <img
                src={CONFIG.logo}
                alt=""
                className="size-8 shrink-0 rounded-md bg-white/90 object-contain p-1"
              />
              <span className="truncate text-base font-semibold">
                {CONFIG.short_title ?? CONFIG.site_title}
              </span>
            </Link>
          )}

          {isMobile ? (
            <Button
              variant="ghost"
              size="icon"
              className={cn("ml-auto", greenIconClass)}
              onClick={closeMobile}
              aria-label="Close menu"
            >
              <ChevronLeft />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className={cn(!isRail && "ml-auto", greenIconClass)}
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <PanelLeft />
            </Button>
          )}
        </div>

        {/* Search */}
        <div className={cn("p-2", isRail && "px-2")}>
          {isRail ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className={cn(
                    "flex w-full items-center justify-center rounded-lg px-2 py-2 transition-colors",
                    bodyIconClass,
                  )}
                  aria-label="Search"
                >
                  <Search className="size-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Search</TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-[#8ba1b7] dark:hover:bg-white/10 dark:hover:text-white"
            >
              <Search className="size-4 shrink-0" />
              <span className="truncate">Search...</span>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
          {navItems.map((item) => {
            const isActive = item.to === activeTo;
            const Icon = item.icon;
            const button = (
              <button
                type="button"
                onClick={() => handleNavigate(item.to)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-brand-primary text-white shadow-sm"
                    : "text-slate-700 hover:bg-slate-200 hover:text-slate-900 dark:text-[#8ba1b7] dark:hover:bg-white/5 dark:hover:text-white",
                  isRail && "justify-center px-2",
                )}
              >
                <Icon className="size-5 shrink-0" />
                {!isRail && <span className="truncate">{item.label}</span>}
              </button>
            );

            if (!isRail) return <div key={item.to}>{button}</div>;

            return (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </nav>

        {/* Footer: user + theme + account */}
        <div
          className={cn(
            "flex bg-brand-primary p-2 text-white",
            isRail ? "flex-col items-center gap-2" : "items-center gap-1",
          )}
        >
          {/* Logged in user */}
          {isRail ? (
            <Tooltip>
              <TooltipTrigger asChild>{avatarNode}</TooltipTrigger>
              <TooltipContent side="right">{displayName}</TooltipContent>
            </Tooltip>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2 px-1">
              {avatarNode}
              <p className="min-w-0 truncate text-sm font-medium">
                {displayName}
              </p>
            </div>
          )}

          {/* Actions */}
          <div
            className={cn(
              "flex items-center gap-1",
              isRail ? "flex-col" : "justify-end",
            )}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={greenIconClass}
                  aria-label="Toggle theme"
                  onClick={toggleTheme}
                >
                  {themeMode === "dark" ? <Sun /> : <Moon />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side={isRail ? "right" : "top"}>
                {themeMode === "dark" ? "Light mode" : "Dark mode"}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={greenIconClass}
                  aria-label="Logout"
                  onClick={() =>
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    })
                  }
                >
                  <LogOut />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={isRail ? "right" : "top"}>
                Logout
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </aside>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

/** Compact trigger shown on mobile to open the sidebar drawer. */
export function SidebarMobileTrigger() {
  const { isMobile, openMobile } = useSidebar();

  if (!isMobile) return null;

  return (
    <div className="sticky top-0 z-30 flex h-14 items-center gap-2 bg-brand-primary px-3 text-white md:hidden">
      <Button
        variant="ghost"
        size="icon"
        className="text-white hover:bg-white/15 hover:text-white"
        onClick={openMobile}
        aria-label="Open menu"
      >
        <Menu />
      </Button>
      <Link to="/" className="flex items-center gap-2 text-white no-underline">
        <img
          src={CONFIG.logo}
          alt=""
          className="size-7 rounded-md bg-white/90 object-contain p-1"
        />
        <span className="text-base font-semibold">
          {CONFIG.short_title ?? CONFIG.site_title}
        </span>
      </Link>
    </div>
  );
}
